import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AREAS, ESCOLARIDADES, JORNADAS, TURNOS } from "@/lib/catalogos";
import {
  guardarPosicion,
  obtenerPosicionAdmin,
  type EntradaPosicion,
} from "@/lib/posiciones.functions";

export const Route = createFileRoute("/_authenticated/admin/posiciones/$id")({
  component: EditorPosicion,
});

const VACIA: EntradaPosicion = {
  nombre: "",
  area: AREAS[0],
  subarea: "",
  turno: TURNOS[0],
  horario: "",
  tipo_jornada: JORNADAS[0],
  vacantes: 1,
  objetivo: "",
  descripcion: "",
  actividades: [],
  perfil: "",
  escolaridad: ESCOLARIDADES[3],
  experiencia: "",
  conocimientos: [],
  competencias: [],
  documentos_indispensables: [],
  reporta_a: "",
  fecha_limite: null,
  rango_salarial: "",
  comentarios: "",
  estatus: "borrador",
};

const aTexto = (lista: string[]) => lista.join("\n");
const aLista = (texto: string) =>
  texto
    .split("\n")
    .map((linea) => linea.trim())
    .filter(Boolean);

function EditorPosicion() {
  const { id } = Route.useParams();
  const esNueva = id === "nueva";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [forma, setForma] = useState<EntradaPosicion>(VACIA);

  const consulta = useQuery({
    queryKey: ["admin", "posicion", id],
    queryFn: () => obtenerPosicionAdmin({ id }),
    enabled: !esNueva,
  });

  useEffect(() => {
    if (consulta.data) {
      const p = consulta.data;
      setForma({
        id: p.id,
        nombre: p.nombre,
        area: p.area,
        subarea: p.subarea ?? "",
        turno: p.turno,
        horario: p.horario ?? "",
        tipo_jornada: p.tipo_jornada ?? JORNADAS[0],
        vacantes: p.vacantes ?? 1,
        objetivo: p.objetivo ?? "",
        descripcion: p.descripcion ?? "",
        actividades: p.actividades ?? [],
        perfil: p.perfil ?? "",
        escolaridad: p.escolaridad ?? "",
        experiencia: p.experiencia ?? "",
        conocimientos: p.conocimientos ?? [],
        competencias: p.competencias ?? [],
        documentos_indispensables: p.documentos_indispensables ?? [],
        reporta_a: p.reporta_a ?? "",
        fecha_limite: p.fecha_limite,
        rango_salarial: p.rango_salarial ?? "",
        comentarios: p.comentarios ?? "",
        estatus: p.estatus,
      });
    }
  }, [consulta.data]);

  const guardar = useMutation({
    mutationFn: (estatus: EntradaPosicion["estatus"]) =>
      guardarPosicion({ ...forma, estatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      queryClient.invalidateQueries({ queryKey: ["posiciones"] });
      toast.success("Posición guardada.");
      navigate({ to: "/admin/posiciones" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const validar = () => {
    if (forma.nombre.trim().length < 3) {
      toast.error("Escribe el nombre de la posición.");
      return false;
    }
    if (!forma.objetivo.trim()) {
      toast.error("Escribe el objetivo del puesto.");
      return false;
    }
    return true;
  };

  const campo = <C extends keyof EntradaPosicion>(clave: C, valor: EntradaPosicion[C]) =>
    setForma((previo) => ({ ...previo, [clave]: valor }));

  if (!esNueva && consulta.isLoading) {
    return <Loader2 className="size-6 animate-spin text-primary" />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/posiciones"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Volver a posiciones
      </Link>
      <h1 className="font-display text-2xl">
        {esNueva ? "Nueva posición" : `Editar: ${forma.nombre}`}
      </h1>

      <div className="grid gap-5 rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <Bloque etiqueta="Nombre de la posición">
          <Input value={forma.nombre} onChange={(e) => campo("nombre", e.target.value)} />
        </Bloque>
        <div className="grid gap-5 sm:grid-cols-2">
          <Bloque etiqueta="Área">
            <Selector
              valor={forma.area}
              opciones={[...AREAS]}
              onChange={(valor) => campo("area", valor)}
            />
          </Bloque>
          <Bloque etiqueta="Subárea (opcional)">
            <Input
              value={forma.subarea ?? ""}
              onChange={(e) => campo("subarea", e.target.value)}
            />
          </Bloque>
          <Bloque etiqueta="Turno">
            <Selector
              valor={forma.turno}
              opciones={[...TURNOS]}
              onChange={(valor) => campo("turno", valor)}
            />
          </Bloque>
          <Bloque etiqueta="Tipo de jornada">
            <Selector
              valor={forma.tipo_jornada}
              opciones={[...JORNADAS]}
              onChange={(valor) => campo("tipo_jornada", valor)}
            />
          </Bloque>
          <Bloque etiqueta="Horario">
            <Input value={forma.horario} onChange={(e) => campo("horario", e.target.value)} />
          </Bloque>
          <Bloque etiqueta="Número de vacantes">
            <Input
              type="number"
              min={1}
              value={forma.vacantes}
              onChange={(e) => campo("vacantes", Number(e.target.value || 1))}
            />
          </Bloque>
          <Bloque etiqueta="Reporta a">
            <Input value={forma.reporta_a} onChange={(e) => campo("reporta_a", e.target.value)} />
          </Bloque>
          <Bloque etiqueta="Fecha límite de postulación">
            <Input
              type="date"
              value={forma.fecha_limite ?? ""}
              onChange={(e) => campo("fecha_limite", e.target.value || null)}
            />
          </Bloque>
        </div>

        <Bloque etiqueta="Objetivo del puesto">
          <Textarea
            rows={3}
            value={forma.objetivo}
            onChange={(e) => campo("objetivo", e.target.value)}
          />
        </Bloque>
        <Bloque etiqueta="Descripción de la posición">
          <Textarea
            rows={4}
            value={forma.descripcion}
            onChange={(e) => campo("descripcion", e.target.value)}
          />
        </Bloque>
        <Bloque etiqueta="Actividades principales (una por línea)">
          <Textarea
            rows={5}
            value={aTexto(forma.actividades)}
            onChange={(e) => campo("actividades", aLista(e.target.value))}
          />
        </Bloque>
        <Bloque etiqueta="Perfil que buscamos">
          <Textarea
            rows={3}
            value={forma.perfil}
            onChange={(e) => campo("perfil", e.target.value)}
          />
        </Bloque>
        <div className="grid gap-5 sm:grid-cols-2">
          <Bloque etiqueta="Escolaridad requerida">
            <Selector
              valor={forma.escolaridad}
              opciones={[...ESCOLARIDADES]}
              onChange={(valor) => campo("escolaridad", valor)}
            />
          </Bloque>
          <Bloque etiqueta="Experiencia requerida">
            <Input
              value={forma.experiencia}
              onChange={(e) => campo("experiencia", e.target.value)}
            />
          </Bloque>
        </div>
        <Bloque etiqueta="Conocimientos técnicos (uno por línea)">
          <Textarea
            rows={4}
            value={aTexto(forma.conocimientos)}
            onChange={(e) => campo("conocimientos", aLista(e.target.value))}
          />
        </Bloque>
        <Bloque etiqueta="Competencias esperadas (una por línea)">
          <Textarea
            rows={4}
            value={aTexto(forma.competencias)}
            onChange={(e) => campo("competencias", aLista(e.target.value))}
          />
        </Bloque>
        <Bloque etiqueta="Documentos indispensables (uno por línea)">
          <Textarea
            rows={4}
            value={aTexto(forma.documentos_indispensables)}
            onChange={(e) => campo("documentos_indispensables", aLista(e.target.value))}
          />
        </Bloque>
        <div className="grid gap-5 sm:grid-cols-2">
          <Bloque etiqueta="Rango salarial (uso interno, opcional)">
            <Input
              value={forma.rango_salarial ?? ""}
              onChange={(e) => campo("rango_salarial", e.target.value)}
            />
          </Bloque>
          <Bloque etiqueta="Comentarios internos (opcional)">
            <Input
              value={forma.comentarios ?? ""}
              onChange={(e) => campo("comentarios", e.target.value)}
            />
          </Bloque>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            disabled={guardar.isPending}
            onClick={() => validar() && guardar.mutate("borrador")}
          >
            Guardar como borrador
          </Button>
          <Button
            disabled={guardar.isPending}
            onClick={() => validar() && guardar.mutate("publicada")}
          >
            {guardar.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Publicar posición
          </Button>
        </div>
      </div>
    </div>
  );
}

function Bloque({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{etiqueta}</Label>
      {children}
    </div>
  );
}

function Selector({
  valor,
  opciones,
  onChange,
}: {
  valor: string;
  opciones: string[];
  onChange: (valor: string) => void;
}) {
  return (
    <Select value={valor} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {opciones.map((opcion) => (
          <SelectItem key={opcion} value={opcion}>
            {opcion}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
